import { c as _c } from "react/compiler-runtime";
import { useQuery, useMutation, useQueryClient, type UseMutationResult, type UseQueryResult } from '@tanstack/react-query';
import * as analytics from 'shared/api/analytics';
import * as budgetOptimser from 'shared/api/budgetOptimiser';
import type * as fetch from 'shared/api/fetch';
import { type ReportType } from 'shared/api/analytics';
import { type AttributionModelReq, type AttributionResp, type IntegrationAttributionsResp, type ScenarioApiResp } from 'shared/api/attributionModels';
import useMutationAnalytics from 'shared/hooks/useMutationAnalytics';
export const queryKeys = {
  budgetOptimiser: 'budgetOptimiser',
  budgetOptimiserDagStatus: 'budgetOptimiserDagStatus',
  budgetOptimiserConfigStatus: 'budgetOptimiserConfigStatus'
};
export const useQueryBudgetOptimisers = clientId => {
  const $ = _c(7);
  let t0;
  let t1;
  if ($[0] !== clientId) {
    t0 = [queryKeys.budgetOptimiser, clientId];
    t1 = async () => (await budgetOptimser.getBudgetOptimiserConfigs(clientId)) as unknown as IntegrationAttributionsResp;
    $[0] = clientId;
    $[1] = t0;
    $[2] = t1;
  } else {
    t0 = $[1];
    t1 = $[2];
  }
  const t2 = Boolean(clientId);
  let t3;
  if ($[3] !== t0 || $[4] !== t1 || $[5] !== t2) {
    t3 = {
      queryKey: t0,
      queryFn: t1,
      enabled: t2
    };
    $[3] = t0;
    $[4] = t1;
    $[5] = t2;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  return useQuery(t3);
};
export const useQueryBudgetOptimiserById = (clientId, configId, t0) => {
  const $ = _c(12);
  const enabled = t0 === undefined ? true : t0;
  let t1;
  let t2;
  if ($[0] !== clientId || $[1] !== configId) {
    t1 = [queryKeys.budgetOptimiser, clientId, configId];
    t2 = async () => await budgetOptimser.getBudgetOptimiserConfig(clientId, configId);
    $[0] = clientId;
    $[1] = configId;
    $[2] = t1;
    $[3] = t2;
  } else {
    t1 = $[2];
    t2 = $[3];
  }
  let t3;
  if ($[4] !== clientId || $[5] !== configId || $[6] !== enabled) {
    t3 = Boolean(clientId) && enabled && !Number.isNaN(configId);
    $[4] = clientId;
    $[5] = configId;
    $[6] = enabled;
    $[7] = t3;
  } else {
    t3 = $[7];
  }
  let t4;
  if ($[8] !== t1 || $[9] !== t2 || $[10] !== t3) {
    t4 = {
      queryKey: t1,
      queryFn: t2,
      enabled: t3
    };
    $[8] = t1;
    $[9] = t2;
    $[10] = t3;
    $[11] = t4;
  } else {
    t4 = $[11];
  }
  return useQuery(t4);
};
export const useMutationDashboard = () => {
  return useMutationAnalytics(_temp);
};
export const useMutationDetailedReport = () => {
  return useMutationAnalytics(_temp2);
};
export const useMutationPreprocData = () => {
  return useMutationAnalytics(_temp3);
};
export const useMutationBudgetOptimizerOptimalsDates = () => {
  return useMutationAnalytics(_temp4);
};
export const useMutationCurrencyConversionRates = () => {
  return useMutationAnalytics(_temp5);
};
export const useQueryBudgetOptimiserDagStatusByModelId = (clientId, modelId, t0, t1, t2) => {
  const $ = _c(10);
  const enabled = t0 === undefined ? true : t0;
  const useCache = t1 === undefined ? true : t1;
  const reportType = t2 === undefined ? "budget_optimiser_non_native_dag" : t2;
  let t3;
  let t4;
  if ($[0] !== clientId || $[1] !== modelId || $[2] !== reportType || $[3] !== useCache) {
    t3 = [queryKeys.budgetOptimiserDagStatus, clientId, modelId, useCache, reportType];
    t4 = async () => await analytics.postConfigStatusById(reportType, clientId, modelId, useCache);
    $[0] = clientId;
    $[1] = modelId;
    $[2] = reportType;
    $[3] = useCache;
    $[4] = t3;
    $[5] = t4;
  } else {
    t3 = $[4];
    t4 = $[5];
  }
  const t5 = Boolean(clientId) && enabled;
  let t6;
  if ($[6] !== t3 || $[7] !== t4 || $[8] !== t5) {
    t6 = {
      queryKey: t3,
      queryFn: t4,
      enabled: t5
    };
    $[6] = t3;
    $[7] = t4;
    $[8] = t5;
    $[9] = t6;
  } else {
    t6 = $[9];
  }
  return useQuery(t6);
};
export const useQueryBudgetOptimiserConfigStatuses = (clientId, t0) => {
  const $ = _c(7);
  const enabled = t0 === undefined ? true : t0;
  let t1;
  let t2;
  if ($[0] !== clientId) {
    t1 = [queryKeys.budgetOptimiserConfigStatus, clientId];
    t2 = async () => await analytics.postConfigStatuses("budget_optimiser_client_configs_status_ui", clientId, true);
    $[0] = clientId;
    $[1] = t1;
    $[2] = t2;
  } else {
    t1 = $[1];
    t2 = $[2];
  }
  let t3;
  if ($[3] !== enabled || $[4] !== t1 || $[5] !== t2) {
    t3 = {
      queryKey: t1,
      queryFn: t2,
      enabled
    };
    $[3] = enabled;
    $[4] = t1;
    $[5] = t2;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  return useQuery(t3);
};
export const useQueryBudgetOptimiserConfigStatusById = (clientId, modelId, t0) => {
  const $ = _c(8);
  const enabled = t0 === undefined ? true : t0;
  let t1;
  let t2;
  if ($[0] !== clientId || $[1] !== modelId) {
    t1 = [queryKeys.budgetOptimiserConfigStatus, clientId, modelId];
    t2 = async () => await analytics.postConfigStatusById("budget_optimiser_config_status_ui", clientId, modelId, true);
    $[0] = clientId;
    $[1] = modelId;
    $[2] = t1;
    $[3] = t2;
  } else {
    t1 = $[2];
    t2 = $[3];
  }
  let t3;
  if ($[4] !== enabled || $[5] !== t1 || $[6] !== t2) {
    t3 = {
      queryKey: t1,
      queryFn: t2,
      enabled
    };
    $[4] = enabled;
    $[5] = t1;
    $[6] = t2;
    $[7] = t3;
  } else {
    t3 = $[7];
  }
  return useQuery(t3);
};
interface UpdateScenariosVars {
  clientId: number;
  configId: number;
  reqBody: AttributionModelReq;
  scenarios: ScenarioApiResp[];
}
export const useMutationUpdateScenarios = () => {
  const $ = _c(2);
  const queryClient = useQueryClient();
  let t0;
  if ($[0] !== queryClient) {
    t0 = {
      mutationFn: _temp6,
      onSuccess: (_data, t1) => {
        const {
          clientId: clientId_0,
          configId: configId_0
        } = t1;
        queryClient.invalidateQueries({
          queryKey: [queryKeys.budgetOptimiser, clientId_0, configId_0]
        });
      }
    };
    $[0] = queryClient;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  return useMutation(t0);
};
export const useMutationBudgetOptimiserScenario = () => {
  const $ = _c(1);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = {
      mutationFn: _temp7
    };
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  return useMutation(t0);
};
async function _temp(data) {
  return await analytics.postBudgetOptimiser(data);
}
async function _temp2(data) {
  return await analytics.postBudgetOptimiserTotals(data);
}
async function _temp3(data) {
  return await analytics.postBudgetOptimiserPreproc(data);
}
async function _temp4(data) {
  return await analytics.postGetDatesInBudgetOptimizerOptimals(data);
}
async function _temp5(data) {
  return await analytics.postGetCurrencyConversionRates(data);
}
async function _temp6(t0) {
  const {
    clientId,
    configId,
    reqBody,
    scenarios
  } = t0;
  return await budgetOptimser.updateBudgetOptimiserScenarios(clientId, configId, reqBody, scenarios);
}
async function _temp7(data) {
  return await analytics.postBudgetOptimiserScenario(data);
}